import React from "react";
import { GoX } from "react-icons/go";

const fmtDate = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("es-AR", { timeZone: "UTC" });
  } catch {
    return "";
  }
};

const fmtDateTime = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("es-AR", {
      timeZone: "America/Argentina/Catamarca",
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return "";
  }
};

const vpnStatusLabel = (status) => {
  switch (status) {
    case "pending":
      return "Pendiente";
    case "active":
      return "Activa";
    case "expired":
      return "Finalizada";
    case "error":
      return "Error";
    case "not_approved":
      return "No aprobada";
    case "not_requested":
    default:
      return "No solicitada";
  }
};

const statusPill = (name = "") => {
  const n = String(name || "").toLowerCase();
  if (n === "pendiente")
    return { bg: "bg-yellow-500", txt: "text-white", br: "border-yellow-300", dot: "bg-yellow-900" };
  if (n === "aprobada")
    return { bg: "bg-custom-green-ccard-1", txt: "text-white", br: "border-green-300", dot: "bg-green-900" };
  if (n === "rechazada")
    return { bg: "bg-red-500", txt: "text-white", br: "border-red-300", dot: "bg-red-900" };
  return { bg: "bg-gray-100", txt: "text-gray-700", br: "border-gray-300", dot: "bg-gray-500" };
};

const Row = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-2 py-1">
    <div className="text-gray-500">{label}</div>
    <div className="col-span-2 font-medium">{value || "—"}</div>
  </div>
);

const WithdrawalDetail = ({ open, onClose, data }) => {
  if (!open || !data) return null;

  const pill = statusPill(data?.status?.name ?? "Pendiente");
  const vpns = (data?.vpns ?? []).map((v) => v.name).join(", ") || "—";
  const items = Array.isArray(data?.items) ? data.items : [];
  const vpnActivation = data?.vpnActivationApproved
    ? `Sí, de ${fmtDateTime(data.vpnActivationStartAt)} a ${fmtDateTime(data.vpnActivationEndAt)} - ${vpnStatusLabel(data.vpnActivationStatus)}`
    : data?.vpnActivationRequested
      ? "Solicitada, pendiente de activación por Sistemas"
      : "No solicitada";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6 relative">
        {/* Cerrar */}
        <button
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <GoX className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold text-gray-800 mb-1">Detalle de Retiro</h2>
        <p className="text-sm text-gray-500 mb-4">ID #{data.id}</p>

        {/* Estado */}
        <div className="mb-4">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${pill.bg} ${pill.txt} ${pill.br}`}
          >
            <span className={`w-2 h-2 rounded-full ${pill.dot}`} />
            {data?.status?.name ?? "Pendiente"}
          </span>
        </div>

        {/* Cabecera */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded-lg p-4 mb-6">
          <Row label="Fecha de Retiro" value={fmtDate(data.withdrawalDate)} />
          <Row label="Fecha de Devolución" value={fmtDate(data.returnDate)} />
          <Row label="Solicitante" value={data?.employee?.screenname} />
          <Row label="Área" value={data?.employee?.area} />
          <Row label="Aprobado por" value={data?.approver?.screenname} />
          <Row label="VPNs" value={vpns} />
          <Row label="Activación VPN" value={vpnActivation} />
          {data?.vpnActivationLastError ? (
            <Row label="Error VPN" value={data.vpnActivationLastError} />
          ) : null}
          <Row label="Motivo" value={data?.reason} />
          <Row label="Comentarios" value={data?.details} />
        </div>

        {/* Ítems */}
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            Ítems ({items.length})
          </h3>
        </div>

        {items.length === 0 ? (
          <div className="text-sm text-gray-500 border rounded p-3">
            No hay ítems asociados a este retiro.
          </div>
        ) : (
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Descripción</th>
                  <th className="text-left p-2">ID Stock</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it.id ?? `${it.stockId}-${idx}`} className="border-t">
                    <td className="p-2">{idx + 1}</td>
                    <td className="p-2">{it.description || "—"}</td>
                    <td className="p-2">{it.stockId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalDetail;
